import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import { generatePath, useHistory, useLocation, useParams } from "react-router-dom";
import { authorization } from "module/auth";
import ROUTES from "config/routing";
import { StyledLoader } from "pages/flyer/builder/drawer/styled";
import { FigmaEditDialog, FigmaEditLookupErrorDialog } from "pages/creative/pageV2/components/FigmaEditDialog";
import { toFigmaEditCopyInput } from "pages/creative/pageV2/components/figmaEditCopy";
import {
  buildPreparedEditSearch,
  FIGMA_DESIGN_SOURCE,
  figmaGateNeedsCheck,
} from "pages/creative/pageV2/utils/builderParams";
import { shouldAutoAddAddressBox, useFigmaEditChoice } from "pages/creative/pageV2/utils/useFigmaEditChoice";
import { getDesignEditInfoGQL, GQLDesign, PreparedDesignForEdit } from "pages/creative/pageV2/useDesignManagement";

type GateStatus = "checking" | "pass" | "choose" | "error";

interface FigmaEditGateProps {
  designId?: string | null;
  // The platform embeds only gate action=edit; the campaign route gates any open of an existing design.
  requireEditAction: boolean;
  // Path to land on for the prepared design (the public route carries the id in the path).
  preparedPathname: (designId: string, design: GQLDesign) => string;
  // The campaign route carries the id in ?designId=, the public route in the path.
  setDesignIdParam: boolean;
  children: React.ReactNode;
}

// Leaving without a choice: platform hosts navigate back on "save"; a standalone tab goes back or closes.
const leaveBuilder = (designId: string, goBack: () => void) => {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage({ type: "save", data: { designId } }, "*");
    return;
  }
  if (window.history.length > 1) goBack();
  else window.close();
};

// Asks copy or flatten before the canvas mounts for a Figma design, so every Edit entry shares one dialog (PAD-32396).
export const FigmaEditGate: React.FC<FigmaEditGateProps> = ({
  designId,
  requireEditAction,
  preparedPathname,
  setDesignIdParam,
  children,
}) => {
  const location = useLocation();
  const history = useHistory();
  // Once a design has passed the gate it stays open for this mount, even when the builder rewrites the URL.
  const passedIdsRef = useRef<Set<string>>(new Set());
  const [checked, setChecked] = useState<{ id: string; status: GateStatus; design?: GQLDesign } | null>(null);
  const [attempt, setAttempt] = useState(0);

  const needsCheck = useMemo(
    () =>
      Boolean(authorization.isLogged?.()) &&
      figmaGateNeedsCheck({ designId, search: location.search, requireEditAction, passedIds: passedIdsRef.current }),
    [designId, location.search, requireEditAction]
  );

  useEffect(() => {
    if (!needsCheck || !designId) return;
    let cancelled = false;
    setChecked({ id: designId, status: "checking" });
    getDesignEditInfoGQL(designId)
      .then((design) => {
        if (cancelled) return;
        if (design?.designSource === FIGMA_DESIGN_SOURCE) {
          setChecked({ id: designId, status: "choose", design });
          return;
        }
        passedIdsRef.current.add(designId);
        setChecked({ id: designId, status: "pass" });
      })
      .catch((error) => {
        // Fail closed: opening an unchecked Figma design would skip the copy or flatten choice and the in use guard.
        console.error(`[FigmaEditGate] design lookup failed for ${designId}`, error);
        if (cancelled) return;
        setChecked({ id: designId, status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [needsCheck, designId, attempt]);

  const onPrepared = useCallback(
    (prepared: PreparedDesignForEdit) => {
      const design = checked?.design;
      if (!design || !designId) return;
      passedIdsRef.current.add(prepared.designId);
      const search = buildPreparedEditSearch({
        designId: prepared.designId,
        fileLink: prepared.fileLink,
        autoAddAddressBox: shouldAutoAddAddressBox({ ...design, id: designId }),
        baseSearch: location.search,
        setDesignIdParam,
      });
      history.replace(`${preparedPathname(prepared.designId, design)}${search}`);
    },
    [checked, designId, history, location.search, preparedPathname, setDesignIdParam]
  );

  const figmaEditChoice = useFigmaEditChoice({ onPrepared });

  if (!needsCheck) return <>{children}</>;

  const current = checked?.id === designId ? checked : null;
  if (current?.status === "pass") return <>{children}</>;

  if (current?.status === "error" && designId) {
    return (
      <>
        <StyledLoader />
        <FigmaEditLookupErrorDialog
          open
          onClose={() => leaveBuilder(designId, () => history.goBack())}
          onRetry={() => setAttempt((value) => value + 1)}
        />
      </>
    );
  }

  if (current?.status === "choose" && current.design && designId) {
    const target = { ...current.design, id: designId };
    return (
      <>
        <StyledLoader />
        <FigmaEditDialog
          open
          {...toFigmaEditCopyInput(current.design)}
          busyChoice={figmaEditChoice.busyChoice}
          showError={figmaEditChoice.showError}
          onClose={() => leaveBuilder(designId, () => history.goBack())}
          onEditCopy={() => figmaEditChoice.choose(target, "copy")}
          onFlattenOriginal={() => figmaEditChoice.choose(target, "flatten")}
        />
      </>
    );
  }

  return (
    <StyledLoader>
      <CircularProgress color="secondary" />
    </StyledLoader>
  );
};

// Platform and platform next open /design/:countryCode/:roomId/:format/builder?action=edit.
export const PublicBuilderFigmaEditGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { roomId, countryCode, format } = useParams() as { roomId?: string; countryCode?: string; format?: string };
  const preparedPathname = useCallback(
    (preparedId: string, design: GQLDesign) =>
      generatePath(ROUTES.PUBLIC_FLYER_BUILDER, {
        countryCode,
        roomId: preparedId,
        // Same fallback the platform uses when it builds the edit URL.
        format: format || design.format || "TEARAWAY",
      }),
    [countryCode, format]
  );
  return (
    <FigmaEditGate designId={roomId} requireEditAction preparedPathname={preparedPathname} setDesignIdParam={false}>
      {children}
    </FigmaEditGate>
  );
};

// The campaign builder route opens an existing design with ?designId=.
export const CampaignBuilderFigmaEditGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const designId = useMemo(() => new URLSearchParams(location.search).get("designId"), [location.search]);
  const preparedPathname = useCallback(() => location.pathname, [location.pathname]);
  return (
    <FigmaEditGate designId={designId} requireEditAction={false} preparedPathname={preparedPathname} setDesignIdParam>
      {children}
    </FigmaEditGate>
  );
};
